/* eslint-disable prettier/prettier*/
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { Box } from '@mui/system';
import { Typography, Button, Dialog, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from 'store';
import { getUser } from 'store/user';
import { IApiRequests, ITimer, useApiRequests, useTimer } from 'hooks';
import { FormInput } from 'components';
import { withOutsideClickPreventer } from 'utils';

import styles from './SmsPhonePopup.module.scss';

type SmsPhonePopupProps = {
  open: boolean;
  handleClose(): void;
  onVerified?(): void;
};

type SendNewPhoneRequest = {
  newPhoneNumber: string;
};

const phoneRegex = /^\+(\d{1}[0-9]{10}|\d{2}[0-9]{6,10}|\d{3}[0-9]{5,9})$/;
const RESEND_TIMER_DURATION = 60;

export const SmsPhonePopup = ({ open, handleClose, onVerified }: SmsPhonePopupProps) => {
  const { t, i18n } = useTranslation('settings');
  const { enqueueSnackbar } = useSnackbar();
  const { seconds, startTimer }: ITimer = useTimer(0);
  const { preUpdateSmsPhone, updateSmsPhone }: IApiRequests = useApiRequests();
  const dispatch = useAppDispatch();

  const [code, setCode] = useState<string>('');
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>('');
  const [enterPhoneOpen, setEnterPhoneOpen] = useState<boolean>(false);
  const [verifyCodeOpen, setVerifyCodeOpen] = useState<boolean>(false);
  const [successOpen, setSuccessOpen] = useState<boolean>(false);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        newPhoneNumber: Yup.string()
          .required(t('phonePopup.newPhoneNumberRequired'))
          .matches(phoneRegex, t('phonePopup.invalidPhoneFormat')),
      }),
    [t],
  );

  const form = useForm<SendNewPhoneRequest>({
    resolver: yupResolver(schema),
    defaultValues: {},
    mode: 'all',
  });

  useEffect(() => {
    if (open) {
      setEnterPhoneOpen(true);
      form.reset({ newPhoneNumber: '+1' });
    }
  }, [open]);

  useEffect(() => {
    if (!enterPhoneOpen) {
      form.reset({ newPhoneNumber: '+1' });
    }
  }, [enterPhoneOpen]);

  const handleSendCode = (): void => {
    const formValues = form.getValues();
    preUpdateSmsPhone(formValues.newPhoneNumber, 'sms', i18n.language)
      .then(() => {
        setNewPhoneNumber(formValues.newPhoneNumber);
        setEnterPhoneOpen(false);
        setVerifyCodeOpen(true);
        startTimer(RESEND_TIMER_DURATION);
      })
      .catch((error: { message: string }) => {
        enqueueSnackbar(t('phonePopup.verificationError', { message: error.message }), { variant: 'error' });
      });
  };

  const handleResend = (): void => {
    preUpdateSmsPhone(newPhoneNumber, 'sms', i18n.language).then(() => {
      enqueueSnackbar(t('phonePopup.codeResent'), { variant: 'info' });
    });
    startTimer(RESEND_TIMER_DURATION);
  };

  const handleCodeChange = (newValue: string): void => {
    setCode(newValue);
    if (newValue.length === 6) handleVerify(newValue);
  };

  const handleVerify = (codeValue?: string): void => {
    updateSmsPhone(newPhoneNumber, codeValue ?? code)
      .then(() => {
        setVerifyCodeOpen(false);
        setCode('');
        setSuccessOpen(true);
        dispatch(getUser());
        onVerified?.();
      })
      .catch((error: any) => {
        setCode('');
        enqueueSnackbar(t('phonePopup.verificationError', { message: error.message }), { variant: 'error' });
      });
  };

  const handleClose_ = (): void => {
    setEnterPhoneOpen(false);
    setVerifyCodeOpen(false);
    setCode('');
    setNewPhoneNumber('');
    handleClose();
  };

  const handleSuccessClose = (): void => {
    setSuccessOpen(false);
    handleClose();
  };

  const dialogSx = {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        width: '100%',
        maxWidth: '350px',
        borderRadius: '10px',
        backgroundColor: 'var(--app-surface)',
        color: 'var(--app-text-default)',
      },
    },
  };

  return (
    <Box>
      <Dialog open={enterPhoneOpen} onClose={withOutsideClickPreventer(handleClose_)} sx={dialogSx}>
        <Box
          className={styles.PhoneMainBox}
          component='form'
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            if (!seconds) handleSendCode();
          }}
        >
          <Box className={styles.changePhone}>
            <Typography variant='body2' fontWeight='bold'>
              {t('phonePopup.enterPhoneNumberUpper')}
            </Typography>
            <CloseIcon onClick={handleClose_} className={styles.closeIcon} />
          </Box>
          <Box>
            <Typography variant='body2' className={styles.desc}>
              {t('phonePopup.sendSmsForNewPhone')}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body2' className={styles.typography}>
              {t('phonePopup.newPhoneNumber')} <span className={styles.asterisk}>*</span>
            </Typography>
            <FormInput name='newPhoneNumber' control={form.control} type='phone' prefix='+1' maxLength={13} />
          </Box>
          {!!seconds && (
            <Box paddingTop='10px' marginBottom='-10px'>
              <Typography variant='body1' textAlign='center' className={styles.resend}>
                {t('phonePopup.resendInSeconds', { seconds })}
              </Typography>
            </Box>
          )}
          <Box className={styles.flexContainer}>
            <Button variant='outlined' className={styles.button} onClick={handleClose_}>
              {t('phonePopup.close')}
            </Button>
            <Button disabled={!!seconds} type='submit' variant='contained' className={styles.button}>
              {t('phonePopup.sendCode')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={verifyCodeOpen} onClose={withOutsideClickPreventer(() => setVerifyCodeOpen(false))} sx={dialogSx}>
        <Box className={styles.PhoneMainBox}>
          <Box className={styles.changePhone}>
            <Typography variant='body2' fontWeight='bold'>
              {t('phonePopup.verifyPhoneNumberUpper')}
            </Typography>
            <CloseIcon onClick={() => setVerifyCodeOpen(false)} className={styles.closeIcon} />
          </Box>
          <Box className={styles.enterPhone}>
            <Typography variant='body2'>{t('phonePopup.enterCodeSentTo')}</Typography>
            <Typography variant='body2' className={styles.phoneNumber} dir='ltr'>
              {newPhoneNumber}
            </Typography>
          </Box>
          <Box>
            <MuiOtpInput
              sx={{
                '& .MuiFormControl-root': {
                  borderColor: 'var(--app-brand-primary)',
                  marginTop: '15px',
                  marginBottom: '15px',
                },
                '& .MuiInputBase-root': { height: '45px' },
                '& .MuiOutlinedInput-notchedOutline': { border: '1px solid var(--app-brand-primary)' },
                '& input': { padding: '0' },
              }}
              autoFocus
              value={code}
              onChange={handleCodeChange}
              length={6}
              TextFieldsProps={{ InputProps: { componentsProps: { input: { pattern: '\\d*' } } } }}
            />
          </Box>
          <Box>
            <Typography variant='body1' textAlign='center' className={styles.resend}>
              {!seconds ? (
                <>
                  {t('phonePopup.didNotReceiveCodeClick')}
                  <u onClick={handleResend}>{t('phonePopup.here')}</u>
                  {t('phonePopup.toResendCode')}
                </>
              ) : (
                <>{t('phonePopup.resendInSeconds', { seconds })}</>
              )}
            </Typography>
          </Box>
          <DialogActions className={styles.cancel}>
            <Button fullWidth onClick={() => setVerifyCodeOpen(false)} variant='outlined'>
              {t('phonePopup.cancel')}
            </Button>
            <Button fullWidth onClick={() => handleVerify()} variant='contained'>
              {t('phonePopup.verify')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={successOpen} onClose={withOutsideClickPreventer(handleSuccessClose)} sx={dialogSx}>
        <Box className={styles.PhoneMainBox}>
          <Box className={styles.PhoneInnerBox}>
            <CheckCircleIcon className={styles.CheckedIcon} />
            <Typography className={styles.PhoneSuccsessTitle} variant='body2'>
              {t('phonePopup.phoneNumberAdded')}
            </Typography>
            <Typography variant='body2'>{t('phonePopup.phoneNumberAddedSuccess')}</Typography>
            <Button variant='contained' onClick={handleSuccessClose}>
              {t('phonePopup.ok')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
